import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
// eslint-disable-next-line no-unused-vars
import { motion } from "framer-motion";
import CreateTripModal from "../../../components/agent/trips/CreateTripModal";
import EnhancedTripRow from "../../../components/agent/trips/EnhancedTripRow";
import DestinationFilter from "../../../components/agent/trips/DestinationFilter";
import StatusFilter from "../../../components/agent/trips/StatusFilter";
import DeleteConfirmationModal from "../../../components/agent/DeleteConfirmationModal";
import TripDocumentUploadModal from "../../../components/agent/trips/TripDocumentUploadModal";
import { usePageAccess } from "../../../hooks/useAccessControl";
import {
  Search,
  Plus,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Filter,
  DollarSign,
  Target,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Castle,
  Ship,
  Globe,
  BarChart3,
  Activity,
  MessageSquare,
} from "lucide-react";
import { useUser } from "../../../context/UserContext";
import usePixieDustNavigation from "../../../hooks/usePixieDustNavigation";
import { TRIP_STATUS } from "../../../utils/statusUtils";
import { decodeHtmlEntities } from "../../../utils/textUtils";
import "../../../styles/modern-design-system.css";
import toast from "../../../utils/toast";

export default function CRMTripManagement() {
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedTripForDocument, setSelectedTripForDocument] = useState(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");

  // Analytics
  const [analytics, setAnalytics] = useState({
    totalTrips: 0,
    destinationBreakdown: {},
    statusBreakdown: {},
    completionRate: 0,
    recentActivity: {
      thisMonth: 0,
      thisWeek: 0
    },
    revenueOverview: {
      totalValue: 0,
      averageValue: 0,
      commissionEarned: 0
    },
    clientEngagement: {
      activeQuestionnaires: 0,
      awaitingResponse: 0
    }
  });
  // eslint-disable-next-line no-unused-vars
  const { user } = useUser();
  const location = useLocation();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  // Pixie dust navigation hook
  const { navigateToUnifiedItinerary } = usePixieDustNavigation();

  const { hasAccess } = usePageAccess('TRIP_MANAGEMENT');

  // Refetch trips when tab/window gains focus (e.g. after client submits questionnaire)
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setRefetchTrigger((k) => k + 1);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch trips
        const tripsRes = await fetch(
          `${import.meta.env.VITE_API_URL}/api/trips`,
          {
            credentials: "include",
          }
        );
        if (!tripsRes.ok) throw new Error("Failed to fetch trips");
        const tripsData = await tripsRes.json();

        // Set trips immediately so UI renders faster
        setTrips(tripsData);
        calculateAnalytics(tripsData);

        // Fetch clients for trip-client mapping
        const clientsRes = await fetch(
          `${import.meta.env.VITE_API_URL}/api/clients`,
          {
            credentials: "include",
          }
        );
        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData);
        }

        // Fetch commission data using bulk endpoint for better performance
        try {
          const tripIds = tripsData.map(trip => trip.tripid);
          
          // Skip bulk commission request if there are no trips
          if (tripIds.length === 0) {
            return; // No trips to fetch commissions for
          }
          
          const bulkCommissionRes = await fetch(
            `${import.meta.env.VITE_API_URL}/api/bulk/trips/commissions`,
            {
              method: 'POST',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ tripIds })
            }
          );

          if (bulkCommissionRes.ok) {
            const bulkData = await bulkCommissionRes.json();
            const commissionMap = new Map();
            
            // Create a map for quick lookup
            bulkData.data.forEach(commission => {
              commissionMap.set(commission.tripid, commission);
            });

            // Merge commission data with trips
            const tripsWithCommissions = tripsData.map(trip => {
              const commissionData = commissionMap.get(trip.tripid);
              return {
                ...trip,
                commission: commissionData?.commission ? {
                  amount: commissionData.commission.amount,
                  rate: commissionData.commission.rate,
                  tripValue: commissionData.trip_value,
                  status: commissionData.commission.status
                } : commissionData?.fallback_commission ? {
                  amount: commissionData.fallback_commission.amount,
                  rate: commissionData.fallback_commission.rate,
                  tripValue: commissionData.trip_value,
                  status: 'pending'
                } : null
              };
            });

            setTrips(tripsWithCommissions);
            calculateAnalytics(tripsWithCommissions);
            console.log(`✅ Loaded commission data for ${bulkData.count} trips via bulk endpoint`);
          } else {
            console.warn(`Bulk commission API returned ${bulkCommissionRes.status}`);
            // Fallback to original trips without commission data
            setTrips(tripsData);
            calculateAnalytics(tripsData);
          }
        } catch (error) {
          console.error('Failed to fetch bulk commission data:', error);
          // Fallback to original trips without commission data
          setTrips(tripsData);
          calculateAnalytics(tripsData);
        }

      } catch (err) {
        console.error("Error loading data:", err);
        toast.error("Failed to load trip data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refetchTrigger]);

  const calculateAnalytics = (tripsData) => {
    const getStatus = (t) => (t?.status ?? t?.Status ?? 'pending').toString().toLowerCase();
    const total = tripsData.length;
    const completed = tripsData.filter((t) => getStatus(t) === TRIP_STATUS.COMPLETED).length;
    const completionRate = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    // Destination breakdown
    const destinationBreakdown = tripsData.reduce((acc, trip) => {
      const dest = trip.destination || 'unknown';
      acc[dest] = (acc[dest] || 0) + 1;
      return acc;
    }, {});

    // Status breakdown (normalize status for API casing)
    const statusBreakdown = tripsData.reduce((acc, trip) => {
      const status = getStatus(trip);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    // Recent activity (last 30 days and last 7 days)
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const getCreatedDate = (trip) => new Date(trip.createdat || trip.created_at || trip.createdAt);

    const thisMonth = tripsData.filter(trip => {
      const createdDate = getCreatedDate(trip);
      return !isNaN(createdDate.getTime()) && createdDate >= thirtyDaysAgo;
    }).length;

    const thisWeek = tripsData.filter(trip => {
      const createdDate = getCreatedDate(trip);
      return !isNaN(createdDate.getTime()) && createdDate >= sevenDaysAgo;
    }).length;

    // Current calendar week (Sun–Sat) bounds
    const dayOfWeek = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    // Total commission for trips in current week (Sun–Sat)
    let totalCommissionThisWeek = 0;
    tripsData.forEach(trip => {
      const createdDate = getCreatedDate(trip);
      if (
        !isNaN(createdDate.getTime()) &&
        createdDate >= startOfWeek &&
        createdDate <= endOfWeek &&
        trip.commission
      ) {
        const commissionAmount = parseFloat(trip.commission.amount) || 0;
        if (!isNaN(commissionAmount) && commissionAmount > 0) {
          totalCommissionThisWeek += commissionAmount;
        }
      }
    });

    // Revenue overview (kept for potential use; totalValue/averageValue retained in state)
    let totalValue = 0;
    let commissionEarned = 0;

    tripsData.forEach(trip => {
      if (trip.commission) {
        const tripValue = parseFloat(trip.commission.tripValue) || 0;
        const commissionAmount = parseFloat(trip.commission.amount) || 0;
        if (!isNaN(tripValue) && tripValue > 0) totalValue += tripValue;
        if (!isNaN(commissionAmount) && commissionAmount > 0) commissionEarned += commissionAmount;
      }
    });

    const averageValue = total > 0 && totalValue > 0 ? Math.round(totalValue / total) : 0;

    // Client engagement (normalize status for API casing)
    const activeQuestionnaires = tripsData.filter(trip => {
      const s = getStatus(trip);
      return s === TRIP_STATUS.PENDING || s === TRIP_STATUS.PLANNING;
    }).length;

    const awaitingResponse = tripsData.filter(trip => 
      getStatus(trip) === TRIP_STATUS.PENDING
    ).length;

    setAnalytics({
      totalTrips: total,
      destinationBreakdown,
      statusBreakdown,
      completionRate,
      recentActivity: {
        thisMonth,
        thisWeek
      },
      revenueOverview: {
        totalValue,
        averageValue,
        commissionEarned,
        totalCommissionThisWeek
      },
      clientEngagement: {
        activeQuestionnaires,
        awaitingResponse
      }
    });
  };

  const handleTripCreated = (newTrip) => {
    setTrips([newTrip, ...trips]);
    calculateAnalytics([newTrip, ...trips]);
    toast.success("Trip created successfully!");
    
    // Get client data for the new trip
    const client = clients.find(c => c.clientid === newTrip.clientid);
    
    // Dispatch event for commission tracking
    window.dispatchEvent(new CustomEvent('tripCreated', {
      detail: { trip: newTrip, client }
    }));
  };

  const handleTripStatusUpdate = (tripId, newStatus) => {
    const updatedTrips = trips.map((trip) =>
      trip.tripid === tripId ? { ...trip, status: newStatus } : trip
    );
    setTrips(updatedTrips);
    calculateAnalytics(updatedTrips);
    
    // Dispatch event for commission tracking
    window.dispatchEvent(new CustomEvent('tripStatusChanged', {
      detail: { tripId, newStatus }
    }));
  };

  const handleClientStatusUpdate = async (clientId, newStatus) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/clients/${clientId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ relationship_status: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update client status");
      }

      // Update local clients state
      const updatedClients = clients.map((client) =>
        client.clientid === clientId
          ? { ...client, relationship_status: newStatus }
          : client
      );
      setClients(updatedClients);
    } catch (error) {
      console.error("Error updating client status:", error);
      toast.error("Failed to update client status");
    }
  };

  const handleCommissionUpdate = async (commissionData) => {
    try {
      // In a real implementation, this would call the backend API
      // For now, we'll just update the local state
      const updatedTrips = trips.map((trip) =>
        trip.tripid === commissionData.tripId
          ? { ...trip, commission: commissionData }
          : trip
      );
      setTrips(updatedTrips);
      calculateAnalytics(updatedTrips);
    } catch (error) {
      console.error("Error updating commission:", error);
      toast.error("Failed to update commission");
    }
  };

  const handleDelete = async (tripId) => {
    try {
      setIsDeleting(true);
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/trips/${tripId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to delete trip");

      const updatedTrips = trips.filter((trip) => trip.tripid !== tripId);
      setTrips(updatedTrips);
      calculateAnalytics(updatedTrips);
      toast.success("Trip deleted successfully!");
      setShowDeleteModal(false);
      setTripToDelete(null);
    } catch (err) {
      console.error("Error deleting trip:", err);
      toast.error("Failed to delete trip");
    } finally {
      setIsDeleting(false);
    }
  };

  const openDeleteModal = (trip) => {
    setTripToDelete(trip);
    setShowDeleteModal(true);
  };

  const handleDocumentUpload = (trip) => {
    setSelectedTripForDocument(trip);
    setShowDocumentModal(true);
  };

  // Helper function to format trip name with proper destination formatting
  const formatTripName = (tripName) => {
    if (!tripName) return '';
    
    // Split the trip name to separate trip name and destination
    const parts = tripName.split(' - ');
    if (parts.length !== 2) return tripName;
    
    const [tripTitle, destination] = parts;
    
    // Format destination names
    const destinationMap = {
      'disney_world': 'Disney World',
      'disneyland': 'Disneyland',
      'universal_orlando': 'Universal Orlando',
      'disney_cruise': 'Disney Cruise',
      'universal_studios': 'Universal Studios',
      'seaworld': 'SeaWorld',
      'legoland': 'Legoland',
      'busch_gardens': 'Busch Gardens',
      'knotts_berry_farm': 'Knott\'s Berry Farm',
      'six_flags': 'Six Flags'
    };
    
    const formattedDestination = destinationMap[destination.toLowerCase()] || destination;
    return `${tripTitle} - ${formattedDestination}`;
  };

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy link. Please try manually.");
    }
  };

  const getClientForTrip = (trip) => {
    return (
      clients.find((client) => client.clientid === trip.clientid) || {
        fullname: trip.fullname || "Unknown Client",
        email: trip.clientemail,
        phone: trip.clientphone,
      }
    );
  };

  // Helper functions for analytics display
  const getDestinationIcon = (destination) => {
    const iconMap = {
      'disney_world': Sparkles,
      'disneyland': Castle,
      'disney_cruise': Ship,
      'universal_orlando': Globe,
      'universal_studios': Globe,
    };
    return iconMap[destination] || Globe;
  };

  const formatDestinationName = (destination) => {
    const nameMap = {
      'disney_world': 'Disney World',
      'disneyland': 'Disneyland',
      'disney_cruise': 'Disney Cruise',
      'universal_orlando': 'Universal Orlando',
      'universal_studios': 'Universal Studios',
    };
    return nameMap[destination] || destination;
  };

  // Normalize trip status (API may return status or Status; handle null/undefined)
  const getTripStatus = (trip) => (trip?.status ?? trip?.Status ?? "pending").toString().toLowerCase();

  const filteredTrips = trips.filter((trip) => {
    const tripName = trip.tripname?.toLowerCase() || "";
    const client = getClientForTrip(trip);
    const clientName = client.fullname?.toLowerCase() || "";

    const matchesSearch =
      tripName.includes(searchQuery.toLowerCase()) ||
      clientName.includes(searchQuery.toLowerCase());

    const matchesDestination =
      selectedDestination === "all" || trip.destination === selectedDestination;
    const matchesStatus =
      statusFilter === "all" || getTripStatus(trip) === statusFilter;

    return matchesSearch && matchesDestination && matchesStatus;
  });

  // Trips that are not completed/cancelled go in the "active" section
  const activeStatuses = ["pending", "confirmed", "planning", "active"];
  const isActiveTrip = (trip) => activeStatuses.includes(getTripStatus(trip));
  const isCompletedOrCancelled = (trip) => ["completed", "cancelled"].includes(getTripStatus(trip));

  if (!hasAccess) {
    return null; // Will redirect
  }

  return (
    <>
      <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="flex flex-col gap-4 justify-between mb-4 md:flex-row md:items-center animate-fade-in-up">
              <div>
                <h1 className="text-xl font-bold tracking-tight leading-tight text-black sm:text-2xl lg:text-3xl text-balance">
                  Trip Management
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  Advanced trip workflow management with status tracking and
                  client integration
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-400 shadow sm:px-5 sm:py-2.5"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Create New Trip</span>
                <span className="sm:hidden">New Trip</span>
              </button>
            </div>

            {/* Compact Analytics Cards */}
            <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-3 lg:grid-cols-6 animate-fade-in-up">
              
              {/* 1. Total Trips */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Total Trips</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.totalTrips}</p>
                <p className="text-xs text-gray-500">All trips</p>
              </div>

              {/* 2. Completion Rate */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <TrendingUp className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Completed Trips</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.completionRate}%</p>
                <div className="mt-1 w-full h-1 bg-gray-200 rounded-full">
                  <div 
                    className="h-1 bg-blue-600 rounded-full transition-all duration-300" 
                    style={{ width: `${analytics.completionRate}%` }}
                  ></div>
                </div>
              </div>

              {/* 3. Pending Trips */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Clock className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Pending</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.statusBreakdown.pending || 0}</p>
                <p className="text-xs text-gray-500">Awaiting action</p>
              </div>

              {/* 4. Recent Activity */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <Activity className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">This Week</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.recentActivity.thisWeek}</p>
                <p className="text-xs text-gray-500">New trips</p>
              </div>

              {/* 5. Total Commission (This Week Sun–Sat) */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <DollarSign className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Total Commission</span>
                </div>
                <p className="text-lg font-bold text-black">
                  ${(analytics.revenueOverview.totalCommissionThisWeek ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-gray-500">This week (Sun–Sat)</p>
              </div>

              {/* 6. Client Engagement */}
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <MessageSquare className="w-4 h-4 text-gray-600" />
                  <span className="text-xs font-medium text-gray-600">Active</span>
                </div>
                <p className="text-xl font-bold text-black">{analytics.clientEngagement.activeQuestionnaires}</p>
                <p className="text-xs text-gray-500">Questionnaires</p>
              </div>

            </div>

            {/* Destination Breakdown - Separate compact section */}
            {Object.keys(analytics.destinationBreakdown).length > 1 && (
              <div className="p-3 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center mb-2">
                  <BarChart3 className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Destination Breakdown</span>
                </div>
                <div className="flex flex-wrap gap-4">
                  {Object.entries(analytics.destinationBreakdown).map(([destination, count]) => {
                    const Icon = getDestinationIcon(destination);
                    return (
                      <div key={destination} className="flex gap-1 items-center">
                        <Icon className="w-3 h-3 text-gray-600" />
                        <span className="text-xs text-gray-600">{formatDestinationName(destination)}</span>
                        <span className="text-xs font-medium text-gray-900">({count})</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Enhanced Filters and Search */}
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-end sm:justify-between animate-fade-in-up">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="relative flex-1 sm:flex-none">
                  <label htmlFor="trip-search" className="sr-only">
                    Search trips
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                    <input
                      id="trip-search"
                      type="text"
                      placeholder="Search all trips..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="py-2 pr-3 pl-10 w-full text-sm rounded-lg border border-gray-300 placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex-1 sm:flex-none sm:w-64">
                  <DestinationFilter
                    selectedDestination={selectedDestination}
                    onDestinationChange={setSelectedDestination}
                  />
                </div>

                <div className="flex flex-col">
                  <button
                    className={`crm-filter-btn ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter size={18} />
                    <span>Filters</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-600 sm:text-sm">
                {filteredTrips.length} of {trips.length} trips
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Trip Status
                    </label>
                    <StatusFilter
                      selectedStatus={statusFilter}
                      onStatusChange={setStatusFilter}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex justify-center py-12 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex gap-2 items-center">
                  <div className="w-6 h-6 rounded-full border-2 border-black animate-spin border-t-transparent"></div>
                  <span className="text-sm text-gray-600">
                    Loading trips...
                  </span>
                </div>
              </div>
            ) : filteredTrips.length === 0 ? (
              /* Empty State */
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex flex-col justify-center items-center px-6 py-12 w-full text-center">
                  <Calendar className="mb-4 w-12 h-12 text-gray-400" />
                  <h2 className="mb-2 text-lg font-semibold text-gray-800 sm:text-xl">
                    No Trips Found
                  </h2>
                  <p className="mb-6 max-w-md text-sm text-gray-600">
                    {searchQuery ||
                    statusFilter !== "all" ||
                    selectedDestination !== "all"
                      ? "Try adjusting your search or filters."
                      : "You haven't created any trips yet. Click 'Create New Trip' to get started."}
                  </p>
                  {!searchQuery &&
                    statusFilter === "all" &&
                    selectedDestination === "all" && (
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="inline-block px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-md shadow transition hover:bg-blue-700 sm:px-5"
                      >
                        Create New Trip
                      </button>
                    )}
                </div>
              </div>
            ) : (
              <>
                {/* Active / Pending Trips Section (pending, confirmed, planning, active, or any non-completed) */}
                {filteredTrips.filter(trip => isActiveTrip(trip) || !isCompletedOrCancelled(trip)).length > 0 && (
                  <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="p-3 border-b border-gray-200 sm:p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="flex gap-2 items-center text-base font-semibold text-black sm:text-lg">
                            Active Trips
                          </h2>
                          <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                            Trips awaiting completion ({filteredTrips.filter(trip => isActiveTrip(trip) || !isCompletedOrCancelled(trip)).length})
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 sm:p-4">
                      <div className="space-y-2 animate-fade-in-up">
                        {filteredTrips
                          .filter(trip => isActiveTrip(trip) || !isCompletedOrCancelled(trip))
                          .map((trip) => (
                            <EnhancedTripRow
                              key={trip.tripid || trip.id}
                              trip={trip}
                              client={getClientForTrip(trip)}
                              onClick={() => navigateToUnifiedItinerary(trip.tripid)}
                              onUnifiedNavigation={navigateToUnifiedItinerary}
                              onDelete={() => openDeleteModal(trip)}
                              onCopyLink={handleCopyLink}
                              onStatusUpdate={handleTripStatusUpdate}
                              onClientStatusUpdate={handleClientStatusUpdate}
                              onCommissionUpdate={handleCommissionUpdate}
                              onDocumentUpload={handleDocumentUpload}
                              hasCRMAccess={hasAccess}
                            />
                          ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Completed Trips Section */}
                {filteredTrips.filter(trip => isCompletedOrCancelled(trip)).length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="p-3 border-b border-gray-200 sm:p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h2 className="flex gap-2 items-center text-base font-semibold text-black sm:text-lg">
                            Completed Trips
                          </h2>
                          <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                            Finished and archived trips ({filteredTrips.filter(trip => isCompletedOrCancelled(trip)).length})
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 sm:p-4">
                      <div className="space-y-2 animate-fade-in-up">
                        {filteredTrips
                          .filter(trip => isCompletedOrCancelled(trip))
                          .map((trip) => (
                            <EnhancedTripRow
                              key={trip.tripid || trip.id}
                              trip={trip}
                              client={getClientForTrip(trip)}
                              onClick={() => navigateToUnifiedItinerary(trip.tripid)}
                              onUnifiedNavigation={navigateToUnifiedItinerary}
                              onDelete={() => openDeleteModal(trip)}
                              onCopyLink={handleCopyLink}
                              onStatusUpdate={handleTripStatusUpdate}
                              onClientStatusUpdate={handleClientStatusUpdate}
                              onCommissionUpdate={handleCommissionUpdate}
                              onDocumentUpload={handleDocumentUpload}
                              hasCRMAccess={hasAccess}
                            />
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

      {/* Create Trip Modal */}
      {showCreateModal && (
        <CreateTripModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onTripCreated={handleTripCreated}
          defaultDestination="disney_world"
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTripToDelete(null);
        }}
        onConfirm={() => handleDelete(tripToDelete?.tripid)}
        title="Delete Trip"
        message="Are you sure you want to delete this trip? This action cannot be undone."
        itemName={tripToDelete ? `${decodeHtmlEntities(tripToDelete.tripname)} - ${tripToDelete.destination}` : ''}
        formatItemName={formatTripName}
        itemType="trip"
        isLoading={isDeleting}
        confirmText="Delete Trip"
        cancelText="Cancel"
      />

      {/* Document Upload Modal */}
      <TripDocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => {
          setShowDocumentModal(false);
          setSelectedTripForDocument(null);
        }}
        tripId={selectedTripForDocument?.tripid}
        clientId={selectedTripForDocument?.clientid}
        clientName={selectedTripForDocument ? getClientForTrip(selectedTripForDocument)?.fullname || getClientForTrip(selectedTripForDocument)?.name || "your client" : "your client"}
        onUploadSuccess={(document) => {
          toast.success(`Document "${document.originalName}" uploaded successfully!`);
        }}
      />
    </>
  );
}